import { useState, useEffect } from "react";

// ─── PLAN META ───────────────────────────────────────────────────────
const PLANS = {
  free:    { max: 1,  label: "Free",    color: "#64748b" },
  pro:     { max: 3,  label: "Pro",     color: "#6366f1" },
  premium: { max: 5,  label: "Premium", color: "#f59e0b" },
  family:  { max: 15, label: "Family",  color: "#22c55e" },
};

const DT_META = {
  browser_extension: { label: "Browser Extension", icon: "🌐", color: "#6366f1" },
  desktop_agent:     { label: "Desktop Agent",      icon: "💻", color: "#f59e0b" },
  mobile_app:        { label: "Mobile App",         icon: "📱", color: "#22c55e" },
  system_service:    { label: "System Service",     icon: "⚙️",  color: "#8b5cf6" },
};

// ─── ICONS ───────────────────────────────────────────────────────────
const IC = {
  Shield:  (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Plus:    (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  X:       (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Edit:    (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Trash:   (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>,
  Power:   (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18.36 6.64a9 9 0 11-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/></svg>,
  Refresh: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>,
  Copy:    (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>,
  Check:   (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  Clock:   (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
};

// ─── HELPERS ─────────────────────────────────────────────────────────
function genToken() {
  const c = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 6 }, () => c[Math.floor(Math.random() * c.length)]).join("");
}

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60)    return "Just now";
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function seedDevices(plan) {
  const now = Date.now();
  const all = [
    { id: "dev_m1", name: "MacBook Pro — Chrome",  type: "browser_extension", status: "active",   os: "macOS 14",     browser: "Chrome 121", appVersion: "2.6.0", lastSeenAt: new Date(now - 30000).toISOString(),    registeredAt: new Date(now - 12 * 864e5).toISOString() },
    { id: "dev_w1", name: "Windows PC — Edge",      type: "desktop_agent",     status: "active",   os: "Windows 11",   browser: "Edge 121",   appVersion: "2.6.0", lastSeenAt: new Date(now - 12e4).toISOString(),     registeredAt: new Date(now - 30 * 864e5).toISOString() },
    { id: "dev_i1", name: "iPhone 15 Pro",          type: "mobile_app",        status: "offline",  os: "iOS 17.3",     browser: null,         appVersion: "2.5.1", lastSeenAt: new Date(now - 36e5).toISOString(),     registeredAt: new Date(now - 5  * 864e5).toISOString() },
    { id: "dev_l1", name: "Linux Workstation",      type: "system_service",    status: "disabled", os: "Ubuntu 24.04", browser: null,         appVersion: "2.4.0", lastSeenAt: new Date(now - 864e5 * 3).toISOString(), registeredAt: new Date(now - 60 * 864e5).toISOString() },
  ];
  const limit = PLANS[plan]?.max || 1;
  return all.slice(0, plan === "free" ? 1 : plan === "pro" ? 2 : Math.min(limit, 3));
}

// ─── QR VISUAL (decorative — not ISO-standard) ───────────────────────
function QRPlaceholder({ value, size = 148 }) {
  const hash = Array.from(value).reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0);
  const grid = 21, cs = size / grid, cells = [];
  const addFinder = (ox, oy) => {
    for (let y = 0; y < 7; y++) for (let x = 0; x < 7; x++) {
      if (x === 0 || x === 6 || y === 0 || y === 6 || (x >= 2 && x <= 4 && y >= 2 && y <= 4))
        cells.push({ x: ox + x, y: oy + y });
    }
  };
  addFinder(0, 0); addFinder(grid - 7, 0); addFinder(0, grid - 7);
  let seed = Math.abs(hash);
  for (let y = 0; y < grid; y++) for (let x = 0; x < grid; x++) {
    if ((x < 8 && y < 8) || (x >= grid - 8 && y < 8) || (x < 8 && y >= grid - 8)) continue;
    seed = (seed * 16807 + 1) % 2147483647;
    if (seed % 3 < 1.5) cells.push({ x, y });
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ borderRadius: 8, display: "block" }}>
      <rect width={size} height={size} fill="#fff" rx="8" />
      {cells.map((c, i) => <rect key={i} x={c.x * cs} y={c.y * cs} width={cs - 0.5} height={cs - 0.5} fill="#111" rx={0.5} />)}
    </svg>
  );
}

// ─── STATUS BADGE ────────────────────────────────────────────────────
const STATUS = {
  active:   { color: "#22c55e", pulse: true },
  offline:  { color: "#64748b", pulse: false },
  disabled: { color: "#ef4444", pulse: false },
  pending:  { color: "#f59e0b", pulse: false },
};

function StatusBadge({ status, label }) {
  const s = STATUS[status] || STATUS.offline;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 5, background: s.color + "14", border: `1px solid ${s.color}22` }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: s.color, flexShrink: 0, animation: s.pulse ? "dmPulse 2s infinite" : "none" }} />
      <span style={{ fontSize: 10, fontWeight: 700, color: s.color, letterSpacing: "0.04em" }}>{label}</span>
    </div>
  );
}

function ActionBtn({ icon, title, onClick, color }) {
  const [hov, setHov] = useState(false);
  const c = color || "var(--text-muted)";
  return (
    <button onClick={onClick} title={title} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${hov ? c + "40" : "var(--border)"}`, background: hov ? c + "12" : "transparent", color: hov ? c : "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s", flexShrink: 0 }}>
      {icon}
    </button>
  );
}

function PairingCountdown({ expiry }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);
  if (!expiry) return null;
  const rem = Math.max(0, Math.floor((expiry - now) / 1000));
  const expired = rem <= 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, background: expired ? "#ef444412" : "var(--hover-bg)", border: "1px solid var(--border)", color: expired ? "#ef4444" : rem < 120 ? "#f59e0b" : "var(--text-muted)", fontSize: 11, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
      <IC.Clock style={{ width: 12, height: 12 }} />
      {expired ? "Expired" : `${String(Math.floor(rem / 60)).padStart(2, "0")}:${String(rem % 60).padStart(2, "0")}`}
    </div>
  );
}

// ─── INLINE TRANSLATIONS ─────────────────────────────────────────────
const DEV_T = {
  en: {
    title: "Devices", subtitle: "Manage all paired devices and pairing tokens",
    pairNew: "Pair New Device", plan: "Plan", usage: "Device Usage",
    slotsRemaining: "slots remaining", upgradePrompt: "Upgrade to add more devices",
    newPairing: "New Device Pairing", connectDevice: "Connect a Device",
    pairingToken: "Pairing Token", setupInstructions: "Setup Instructions",
    step1: "Install AbsoluteGuard on your device",   step1sub: "Browser extension, desktop agent, or mobile app",
    step2: "Open the app and tap 'Pair Device'",     step2sub: "Scan the QR code or enter the token manually",
    step3: "Enter the pairing token shown here",     step3sub: "Token expires in 15 minutes — one use only",
    simulatePair: "Simulate Pairing (Demo)",
    noDevices: "No devices paired yet", noDevicesSub: "Click \"Pair New Device\" to get started",
    lastSeen: "Last Seen", registered: "Registered",
    rename: "Rename", disable: "Disable", enable: "Enable", revoke: "Revoke",
    revokeConfirm: "Revoke this device? This cannot be undone.",
    recentEvents: "Recent Device Events",
    privacyNote: "One-time tokens · Hashed storage · 30s heartbeat · Auto-offline after 5 min",
    regenerate: "Regenerate", copy: "Copy", copied: "Copied", scanWithApp: "Scan with AG app",
    limitReached: "Device limit reached. Upgrade your plan to add more.",
    active: "Active", offline: "Offline", disabled: "Disabled", pending: "Pending",
  },
  de: {
    title: "Geräte", subtitle: "Alle gekoppelten Geräte und Pairing-Token verwalten",
    pairNew: "Neues Gerät koppeln", plan: "Plan", usage: "Gerätenutzung",
    slotsRemaining: "Slots verfügbar", upgradePrompt: "Upgrade für mehr Geräte",
    newPairing: "Neues Gerät koppeln", connectDevice: "Gerät verbinden",
    pairingToken: "Pairing-Token", setupInstructions: "Einrichtungsanleitung",
    step1: "AbsoluteGuard auf Gerät installieren",    step1sub: "Browser-Erweiterung, Desktop-Agent oder mobile App",
    step2: "App öffnen und 'Gerät koppeln' tippen",  step2sub: "QR-Code scannen oder Token manuell eingeben",
    step3: "Den hier angezeigten Token eingeben",     step3sub: "Token läuft in 15 Min ab — einmalig",
    simulatePair: "Kopplung simulieren (Demo)",
    noDevices: "Noch keine Geräte gekoppelt", noDevicesSub: "Klicke auf \"Neues Gerät koppeln\"",
    lastSeen: "Zuletzt gesehen", registered: "Registriert",
    rename: "Umbenennen", disable: "Deaktivieren", enable: "Aktivieren", revoke: "Widerrufen",
    revokeConfirm: "Gerät widerrufen? Nicht rückgängig machbar.",
    recentEvents: "Letzte Geräteereignisse",
    privacyNote: "Einmalige Token · Gehashte Speicherung · 30s Heartbeat · Auto-Offline nach 5 Min",
    regenerate: "Neu generieren", copy: "Kopieren", copied: "Kopiert", scanWithApp: "Mit AG-App scannen",
    limitReached: "Gerätelimit erreicht. Upgrade für mehr.",
    active: "Aktiv", offline: "Offline", disabled: "Deaktiviert", pending: "Ausstehend",
  },
  bg: {
    title: "Устройства", subtitle: "Управлявай сдвоените устройства и токени",
    pairNew: "Сдвои ново устройство", plan: "План", usage: "Използване",
    slotsRemaining: "налични слота", upgradePrompt: "Надгради за повече устройства",
    newPairing: "Ново сдвояване", connectDevice: "Свържи устройство",
    pairingToken: "Токен за сдвояване", setupInstructions: "Инструкции за настройка",
    step1: "Инсталирай AbsoluteGuard",               step1sub: "Разширение, настолен агент или мобилно приложение",
    step2: "Отвори приложението и натисни Сдвои",    step2sub: "Сканирай QR кода или въведи токена ръчно",
    step3: "Въведи показания тук токен",             step3sub: "Изтича след 15 минути — еднократна употреба",
    simulatePair: "Симулирай сдвояване (Демо)",
    noDevices: "Все още няма сдвоени устройства", noDevicesSub: "Натисни Сдвои ново устройство",
    lastSeen: "Последно виждан", registered: "Регистриран",
    rename: "Преименувай", disable: "Деактивирай", enable: "Активирай", revoke: "Отмени",
    revokeConfirm: "Да се отмени устройството? Не може да се върне.",
    recentEvents: "Последни събития на устройства",
    privacyNote: "Еднократни токени · Хеширано съхранение · 30с heartbeat · Авто-офлайн след 5 мин",
    regenerate: "Регенерирай", copy: "Копирай", copied: "Копирано", scanWithApp: "Сканирай с AG приложение",
    limitReached: "Достигнат лимит. Надгради плана.",
    active: "Активно", offline: "Офлайн", disabled: "Деактивирано", pending: "Изчакващо",
  },
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────
export default function DeviceManagement({ lang = "en" }) {
  const t = DEV_T[lang] || DEV_T.en;
  const T = (k) => t[k] ?? k;

  const [plan,          setPlan]          = useState("pro");
  const [devices,       setDevices]       = useState(() => seedDevices("pro"));
  const [pairingToken,  setPairingToken]  = useState(null);
  const [pairingExpiry, setPairingExpiry] = useState(null);
  const [showPairing,   setShowPairing]   = useState(false);
  const [editingId,     setEditingId]     = useState(null);
  const [editName,      setEditName]      = useState("");
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [copied,        setCopied]        = useState(false);
  const [toast,         setToast]         = useState(null);
  const [log,           setLog]           = useState([]);
  const [visible,       setVisible]       = useState(false);

  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  // Heartbeat — proper cleanup on unmount
  useEffect(() => {
    const iv = setInterval(() => {
      setDevices(prev => prev.map(d =>
        d.status === "active" ? { ...d, lastSeenAt: new Date().toISOString() } : d
      ));
    }, 30000);
    return () => clearInterval(iv);
  }, []);

  // Reseed when plan changes
  useEffect(() => {
    setDevices(seedDevices(plan));
    setShowPairing(false);
    setPairingToken(null);
  }, [plan]);

  const pi = PLANS[plan];
  const activeDevices = devices.filter(d => d.status !== "disabled");
  const usage = { used: activeDevices.length, limit: pi.max, remaining: Math.max(0, pi.max - activeDevices.length), atLimit: activeDevices.length >= pi.max };

  const showToast = (msg, type = "info") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const logEvent  = (type, name, action) => setLog(prev => [{ id: Date.now(), type, deviceName: name, action, time: new Date().toISOString() }, ...prev].slice(0, 20));

  const startPairing = async () => {
    if (usage.atLimit) { showToast(T("limitReached"), "error"); return; }
    try {
      const API = window.AG_API_BASE || "";
      const r = await fetch(`${API}/api/devices/pairing-token`, { method: "POST", headers: { "Content-Type": "application/json" } });
      const d = await r.json();
      if (d.token) { setPairingToken(d.token); setPairingExpiry(new Date(d.expiresAt).getTime()); setShowPairing(true); return; }
    } catch {}
    setPairingToken(genToken()); setPairingExpiry(Date.now() + 15 * 60 * 1000); setShowPairing(true);
  };

  const regenerateToken = async () => {
    try {
      const API = window.AG_API_BASE || "";
      const r = await fetch(`${API}/api/devices/pairing-token`, { method: "POST", headers: { "Content-Type": "application/json" } });
      const d = await r.json();
      if (d.token) { setPairingToken(d.token); setPairingExpiry(new Date(d.expiresAt).getTime()); setCopied(false); showToast("Token regenerated"); return; }
    } catch {}
    setPairingToken(genToken()); setPairingExpiry(Date.now() + 15 * 60 * 1000); setCopied(false); showToast("Token regenerated");
  };

  const copyToken = () => {
    if (!pairingToken) return;
    navigator.clipboard?.writeText(pairingToken).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const simulatePair = () => {
    if (!pairingToken) return;
    const types = Object.keys(DT_META);
    const oses = ["macOS 15", "Windows 11", "Ubuntu 24", "Chrome OS", "Android 15", "iOS 18"];
    const browsers = ["Chrome 122", "Firefox 124", "Safari 18", "Edge 122", "Brave 1.62"];
    const type = types[Math.floor(Math.random() * types.length)];
    const os = oses[Math.floor(Math.random() * oses.length)];
    const browser = type === "browser_extension" ? browsers[Math.floor(Math.random() * browsers.length)] : null;
    const now = new Date().toISOString();
    const nd = { id: "dev_" + Date.now().toString(36), name: browser ? `${browser} on ${os}` : `${os} Agent`, type, status: "active", os, browser, appVersion: "2.6.0", lastSeenAt: now, registeredAt: now };
    setDevices(prev => [...prev, nd]);
    setShowPairing(false); setPairingToken(null);
    logEvent("DeviceRegistered", nd.name, "registered");
    showToast(`${nd.name} paired successfully`, "success");
  };

  const renameDevice = (id) => {
    if (!editName.trim()) return;
    setDevices(prev => prev.map(d => d.id === id ? { ...d, name: editName.trim() } : d));
    setEditingId(null); setEditName(""); showToast("Device renamed");
  };

  const toggleDisable = (id) => {
    setDevices(prev => prev.map(d => {
      if (d.id !== id) return d;
      const ns = d.status === "disabled" ? "active" : "disabled";
      logEvent("PolicyChanged", d.name, ns);
      return { ...d, status: ns, lastSeenAt: new Date().toISOString() };
    }));
  };

  const revokeDevice = (id) => {
    const dev = devices.find(d => d.id === id);
    if (dev) logEvent("DeviceRevoked", dev.name, "revoked");
    setDevices(prev => prev.filter(d => d.id !== id));
    setConfirmRevoke(null); showToast("Device revoked", "error");
  };

  const STATUS_LABEL = { active: T("active"), offline: T("offline"), disabled: T("disabled"), pending: T("pending") };

  return (
    <div style={{ padding: "32px 36px", maxWidth: 1000, opacity: visible ? 1 : 0, transition: "opacity .4s ease" }}>

      <style>{`
        @keyframes dmFadeUp { from { opacity:0;transform:translateY(10px) } to { opacity:1;transform:translateY(0) } }
        @keyframes dmSlideDown { from { opacity:0;transform:translateY(-8px) } to { opacity:1;transform:translateY(0) } }
        @keyframes dmPulse { 0%,100%{opacity:1}50%{opacity:.4} }
        @keyframes dmToastIn { from{opacity:0;transform:translateX(-50%) translateY(16px)}to{opacity:1;transform:translateX(-50%) translateY(0)} }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)", zIndex: 9999, padding: "10px 22px", borderRadius: 10, background: "var(--card-bg)", border: `1px solid ${toast.type === "success" ? "#22c55e40" : toast.type === "error" ? "#ef444440" : "var(--border)"}`, color: toast.type === "success" ? "#22c55e" : toast.type === "error" ? "#ef4444" : "var(--text)", fontSize: 13, fontWeight: 600, boxShadow: "0 8px 32px rgba(0,0,0,.2)", animation: "dmToastIn .25s ease", backdropFilter: "blur(12px)", whiteSpace: "nowrap" }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "var(--text)", margin: "0 0 4px", letterSpacing: "-0.03em" }}>{T("title")}</h1>
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>{T("subtitle")}</p>
        </div>
        <button onClick={startPairing} disabled={usage.atLimit}
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 20px", borderRadius: 10, border: "none", cursor: usage.atLimit ? "not-allowed" : "pointer", background: usage.atLimit ? "var(--border)" : "linear-gradient(135deg, var(--accent), var(--accent-h))", color: usage.atLimit ? "var(--text-muted)" : "#fff", fontSize: 13, fontWeight: 700, boxShadow: usage.atLimit ? "none" : "0 4px 16px var(--accent-g)", opacity: usage.atLimit ? 0.6 : 1, transition: "all .15s" }}>
          <IC.Plus style={{ width: 16, height: 16 }} />{T("pairNew")}
        </button>
      </div>

      {/* Plan + Usage */}
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 14, marginBottom: 24 }}>
        <div style={{ padding: "16px 20px", borderRadius: 12, background: "var(--card-bg)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>{T("plan")}</div>
          <div style={{ display: "flex", gap: 5 }}>
            {Object.entries(PLANS).map(([k, v]) => (
              <button key={k} onClick={() => setPlan(k)}
                style={{ padding: "6px 13px", borderRadius: 7, border: `1px solid ${plan === k ? v.color + "50" : "var(--border)"}`, background: plan === k ? v.color + "14" : "transparent", color: plan === k ? v.color : "var(--text-muted)", cursor: "pointer", fontSize: 12, fontWeight: 600, transition: "all .15s" }}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ padding: "16px 24px", borderRadius: 12, background: "var(--card-bg)", border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 24 }}>
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 6 }}>{T("usage")}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span style={{ fontSize: 32, fontWeight: 800, color: usage.atLimit ? "#ef4444" : pi.color, letterSpacing: "-0.03em", lineHeight: 1 }}>{usage.used}</span>
              <span style={{ fontSize: 15, color: "var(--text-muted)", fontWeight: 500 }}>/ {usage.limit}</span>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ height: 6, borderRadius: 3, background: "var(--border)", overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 3, width: `${Math.min(100, (usage.used / Math.max(usage.limit, 1)) * 100)}%`, background: usage.atLimit ? "#ef4444" : pi.color, transition: "width .5s cubic-bezier(.16,1,.3,1)" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{usage.remaining} {T("slotsRemaining")}</span>
              {usage.atLimit && <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 600 }}>{T("upgradePrompt")}</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 14, flexShrink: 0 }}>
            {Object.entries(DT_META).map(([type, meta]) => {
              const count = devices.filter(d => d.type === type && d.status !== "disabled").length;
              if (!count) return null;
              return (
                <div key={type} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 18, lineHeight: 1, marginBottom: 2 }}>{meta.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: meta.color }}>{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pairing panel */}
      {showPairing && (
        <div style={{ marginBottom: 22, padding: 28, borderRadius: 14, background: "var(--card-bg)", border: "1px solid var(--accent-g)", animation: "dmSlideDown .3s ease", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "var(--accent)", opacity: 0.04, filter: "blur(50px)", pointerEvents: "none" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "var(--accent)", marginBottom: 4, textTransform: "uppercase" }}>{T("newPairing")}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text)" }}>{T("connectDevice")}</div>
            </div>
            <button onClick={() => { setShowPairing(false); setPairingToken(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", padding: 4 }}>
              <IC.X style={{ width: 18, height: 18 }} />
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "168px 1fr 1fr", gap: 28, alignItems: "start" }}>
            {/* QR */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
              <div style={{ padding: 10, background: "#fff", borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,.12)" }}>
                <QRPlaceholder value={`absoluteguard://pair?token=${pairingToken}`} size={148} />
              </div>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{T("scanWithApp")}</span>
            </div>

            {/* Token */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>{T("pairingToken")}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderRadius: 10, background: "var(--hover-bg)", border: "1px dashed var(--accent-g)", marginBottom: 12 }}>
                <span style={{ fontSize: 28, fontWeight: 800, letterSpacing: "0.22em", color: "var(--accent)", flex: 1, fontVariantNumeric: "tabular-nums" }}>
                  {pairingToken?.slice(0, 3)}<span style={{ color: "var(--text-muted)", margin: "0 2px", fontWeight: 300 }}>–</span>{pairingToken?.slice(3)}
                </span>
                <button onClick={copyToken}
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: `1px solid ${copied ? "#22c55e40" : "var(--border)"}`, background: copied ? "#22c55e10" : "var(--card-bg)", color: copied ? "#22c55e" : "var(--text-muted)", cursor: "pointer", fontSize: 11, fontWeight: 600, transition: "all .15s" }}>
                  {copied ? <><IC.Check style={{ width: 12, height: 12 }} />{T("copied")}</> : <><IC.Copy style={{ width: 12, height: 12 }} />{T("copy")}</>}
                </button>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={regenerateToken}
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", cursor: "pointer", fontSize: 11, fontWeight: 500, transition: "all .15s" }}>
                  <IC.Refresh style={{ width: 12, height: 12 }} />{T("regenerate")}
                </button>
                <PairingCountdown expiry={pairingExpiry} />
              </div>
            </div>

            {/* Instructions */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 12 }}>{T("setupInstructions")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { n: "1", text: T("step1"), sub: T("step1sub") },
                  { n: "2", text: T("step2"), sub: T("step2sub") },
                  { n: "3", text: T("step3"), sub: T("step3sub") },
                ].map(s => (
                  <div key={s.n} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ width: 22, height: 22, borderRadius: 6, background: "var(--accent-a)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{s.n}</div>
                    <div>
                      <div style={{ fontSize: 12, color: "var(--text)", fontWeight: 500, lineHeight: 1.4 }}>{s.text}</div>
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 1 }}>{s.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={simulatePair}
                style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: "1px dashed #22c55e30", background: "#22c55e08", color: "#22c55e", cursor: "pointer", fontSize: 11, fontWeight: 600, transition: "all .15s" }}>
                ⚡ {T("simulatePair")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Device cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {devices.length === 0 && (
          <div style={{ padding: 52, textAlign: "center", borderRadius: 14, background: "var(--card-bg)", border: "1px dashed var(--border)" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📱</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 6 }}>{T("noDevices")}</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{T("noDevicesSub")}</div>
          </div>
        )}
        {devices.map((device, idx) => {
          const tm = DT_META[device.type] || DT_META.browser_extension;
          const isEditing  = editingId     === device.id;
          const isRevoking = confirmRevoke === device.id;
          return (
            <div key={device.id}
              style={{ padding: "18px 22px", borderRadius: 12, background: isRevoking ? "#ef444408" : "var(--card-bg)", border: `1px solid ${isRevoking ? "#ef444428" : "var(--border)"}`, opacity: device.status === "disabled" ? 0.55 : 1, transition: "all .2s", animation: `dmFadeUp .3s ease ${idx * 0.04}s both` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: tm.color + "12", border: `1px solid ${tm.color}20`, fontSize: 19, flexShrink: 0 }}>{tm.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    {isEditing ? (
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === "Enter" && renameDevice(device.id)} autoFocus
                          style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--accent-g)", background: "var(--input-bg)", color: "var(--text)", fontSize: 14, fontWeight: 600, width: 220, outline: "none" }} />
                        <button onClick={() => renameDevice(device.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#22c55e", display: "flex" }}><IC.Check style={{ width: 15, height: 15 }} /></button>
                        <button onClick={() => setEditingId(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", display: "flex" }}><IC.X style={{ width: 15, height: 15 }} /></button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{device.name}</span>
                    )}
                    <StatusBadge status={device.status} label={STATUS_LABEL[device.status] || device.status} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--text-muted)", flexWrap: "wrap" }}>
                    <span>{tm.label}</span><span>·</span>
                    <span>{device.os}</span>
                    {device.browser && <><span>·</span><span>{device.browser}</span></>}
                    <span>·</span><span>v{device.appVersion}</span>
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0, marginRight: 12 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 3 }}>{T("lastSeen")}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: device.status === "active" ? "#22c55e" : "var(--text-muted)" }}>{timeAgo(device.lastSeenAt)}</div>
                </div>
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  <ActionBtn icon={<IC.Edit style={{ width: 13, height: 13 }} />} title={T("rename")} onClick={() => { setEditingId(device.id); setEditName(device.name); }} />
                  <ActionBtn icon={<IC.Power style={{ width: 13, height: 13 }} />} title={device.status === "disabled" ? T("enable") : T("disable")} color={device.status === "disabled" ? "#22c55e" : "#f59e0b"} onClick={() => toggleDisable(device.id)} />
                  <ActionBtn icon={<IC.Trash style={{ width: 13, height: 13 }} />} title={T("revoke")} color="#ef4444" onClick={() => setConfirmRevoke(device.id)} />
                </div>
              </div>
              {isRevoking && (
                <div style={{ marginTop: 14, padding: "12px 16px", borderRadius: 8, background: "#ef444408", border: "1px solid #ef444428", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, animation: "dmFadeUp .2s ease" }}>
                  <span style={{ fontSize: 12, color: "#ef4444", fontWeight: 500 }}>Revoke "{device.name}"? {T("revokeConfirm").split("?")[1]?.trim() || ""}</span>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => revokeDevice(device.id)} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{T("revoke")}</button>
                    <button onClick={() => setConfirmRevoke(null)} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", cursor: "pointer", fontSize: 11 }}>Cancel</button>
                  </div>
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 10, color: "var(--text-muted)", opacity: 0.5 }}>
                {T("registered")} {new Date(device.registeredAt).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" })} · ID: {device.id}
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent events log */}
      {log.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>{T("recentEvents")}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {log.slice(0, 8).map(evt => (
              <div key={evt.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px", borderRadius: 8, background: "var(--card-bg)", border: "1px solid var(--border)", fontSize: 11 }}>
                <span style={{ fontWeight: 700, color: evt.type === "DeviceRevoked" ? "#ef4444" : evt.type === "DeviceRegistered" ? "#22c55e" : "#f59e0b", minWidth: 130 }}>{evt.type}</span>
                <span style={{ color: "var(--text-secondary)" }}>{evt.deviceName}</span>
                <span style={{ color: "var(--text-muted)" }}>· {evt.action}</span>
                <span style={{ marginLeft: "auto", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{new Date(evt.time).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ textAlign: "center", marginTop: 48, fontSize: 10, color: "var(--text-muted)", opacity: 0.4, letterSpacing: "0.06em" }}>
        🔒 {T("privacyNote")}
      </div>
    </div>
  );
}
